import { useEffect, useRef } from "react";

/**
 * useAutoRefresh — invoke `loadFn` immediately and then every `intervalMs`.
 *   - Pauses when the browser tab is hidden (`document.hidden`) to save bandwidth.
 *   - Re-fires immediately when the tab becomes visible again after being hidden.
 *   - Guarantees the callback runs at most once concurrently even if it's async
 *     and slow (skips a tick if the previous invocation hasn't finished).
 *
 * Usage:
 *   useAutoRefresh(async () => {
 *     const r = await api.get("/some/endpoint");
 *     setState(r.data);
 *   }, 15000);
 */
export function useAutoRefresh(loadFn, intervalMs = 15000) {
  const busy = useRef(false);
  const fnRef = useRef(loadFn);
  fnRef.current = loadFn;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled || busy.current || document.hidden) return;
      busy.current = true;
      try { await fnRef.current(); } catch (e) { /* swallow */ }
      finally { busy.current = false; }
    };
    // Initial fire
    run();
    const id = setInterval(run, intervalMs);
    // When user comes back to the tab, refresh immediately
    const onVis = () => { if (!document.hidden) run(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs]);
}
